import { DefaultFocusStatusModel } from "../../../models/DefaultFocusStatusModel";
import { QuestionType } from "../../../models/enums/QuestionType";
import { isFocusable } from "../../../models/utilities/defaultFocusStatusUtilities";
import { setDefaultFocusStatus } from "../../../redux/actions";

function isTextLikeInputOrTextarea(el: HTMLElement): el is HTMLInputElement | HTMLTextAreaElement {
    if (el.tagName === "TEXTAREA") {
        return true;
    }
    if (el.tagName !== "INPUT") {
        return false;
    }
    const type = ((el as HTMLInputElement).type || "text").toLowerCase();
    switch (type) {
        case "text":
        case "search":
        case "tel":
        case "url":
        case "email":
        case "password":
        case "number":
        case "date":
        case "datetime-local":
        case "time":
        case "month":
        case "week":
        case "":
            return true;
        default:
            return false;
    }
}

/**
 * When true, horizontal arrows should edit text instead of moving grid focus.
 * At the logical start (ArrowLeft) or end (ArrowRight) of the value — including empty fields —
 * returns false so grid column navigation still runs.
 */
function shouldDeferHorizontalGridNavForTextControl(event: React.KeyboardEvent): boolean {
    const key = event.key;
    if (key !== "ArrowLeft" && key !== "ArrowRight") {
        return false;
    }
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
        return false;
    }
    if (target.isContentEditable) {
        return true;
    }
    if (!isTextLikeInputOrTextarea(target)) {
        return false;
    }
    const input = target;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    if (start === null || end === null) {
        return true;
    }
    const len = input.value?.length ?? 0;
    if (start !== end) {
        return true;
    }
    if (key === "ArrowLeft") {
        return start > 0;
    }
    return start < len;
}

export abstract class KeyboardNavigation {
    protected defaultFocusStatus;
    protected dispatch;
    protected question;
    protected isHybridGrid;
    protected isRunningTotal;

    constructor(defaultFocusStatus, dispatch, question) {
        this.defaultFocusStatus = defaultFocusStatus;
        this.dispatch = dispatch;
        this.question = question;
        this.isHybridGrid =
            this.question &&
            this.question.parent &&
            this.question.parent.type === QuestionType.HybridGrid;
        this.isRunningTotal =
            this.question &&
            this.question.parent &&
            this.question.parent.type === QuestionType.RunningTotal;
    }

    abstract findNextFocusableElementWithinSameVariable(
        currentElement: DefaultFocusStatusModel,
        eventKey: string,
    ): DefaultFocusStatusModel | null;

    abstract findNextFocusableElementWithinNextVariable(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null;

    abstract findLastFocusableElementWithinPreviousVariable(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null;

    findNextFocusableElementWithinNextVariableCommon(
        currentElement: DefaultFocusStatusModel,
        variableIds: number[] = null,
    ): DefaultFocusStatusModel | null {
        if (this.isHybridGrid) {
            return this.findNextFocusableElementWithinNextVariableCommonForHybridGrid(
                currentElement,
            );
        }

        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);
        for (let i = index + 1, len = this.defaultFocusStatus.statuses.length; i < len; i++) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];

            if (elementFocusStatus.elementId.includes("IMAGE_CHOICE_GRID")) {
                let elementRef = document.getElementById(elementFocusStatus.elementId);
                if (elementRef) {
                    elementFocusStatus.elementRef.current = elementRef;
                }
            }

            const variableCondition =
                variableIds && variableIds.length > 0
                    ? !variableIds.includes(elementFocusStatus.variableId)
                    : elementFocusStatus.variableId !== currentElement.variableId;
            if (
                elementFocusStatus.elementRef?.current &&
                isFocusable(elementFocusStatus) &&
                ((elementFocusStatus.variableId !== 0 && variableCondition) ||
                    (elementFocusStatus.variableId === currentElement.variableId &&
                        elementFocusStatus.elementRef?.current?.tagName === "TEXTAREA"))
            ) {
                return this.getFocusableElement(elementFocusStatus, true);
            }
        }
        return null;
    }

    findNextFocusableElementWithinNextVariableCommonForHybridGrid(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);
        for (let i = index + 1, len = this.defaultFocusStatus.statuses.length; i < len; i++) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];

            if (
                elementFocusStatus.questionId !== 0 &&
                elementFocusStatus.questionId !== currentElement.questionId &&
                elementFocusStatus.elementRef?.current
            ) {
                return this.getFocusableElement(elementFocusStatus, true);
            }
        }
        return null;
    }

    findLastFocusableElementWithinPreviousVariableCommon(
        currentElement: DefaultFocusStatusModel,
        variableIds: number[] = null,
    ): DefaultFocusStatusModel | null {
        if (this.isHybridGrid) {
            return this.findLastFocusableElementWithinPreviousVariableCommonForHybridGrid(
                currentElement,
            );
        }

        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);
        for (let i = index - 1; i >= 0; i--) {
            const elementFocusStatus: DefaultFocusStatusModel = this.defaultFocusStatus.statuses[i];

            if (elementFocusStatus.elementId.includes("IMAGE_CHOICE_GRID")) {
                let elementRef = document.getElementById(elementFocusStatus.elementId);
                if (elementRef) {
                    elementFocusStatus.elementRef.current = elementRef;
                }
            }

            const variableCondition =
                variableIds && variableIds.length > 0
                    ? !variableIds.includes(elementFocusStatus.variableId)
                    : elementFocusStatus.variableId !== currentElement.variableId;

            if (
                elementFocusStatus.variableId !== 0 &&
                variableCondition &&
                elementFocusStatus.elementRef?.current &&
                isFocusable(elementFocusStatus)
            ) {
                return this.getFocusableElement(elementFocusStatus);
            }
        }
        return null;
    }

    findLastFocusableElementWithinPreviousVariableCommonForHybridGrid(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);

        /* Shift+Tab must mirror Tab forward. Tab stays in-grid via findNextCellWithinSameGrid
         * when Hybrid "next variable" returns null; without a same-question walk here,
         * Shift+Tab from mid-row columns (Text/Slider/etc.) jumps straight to the previous
         * question and never reaches findPreviousCellWithinSameGrid. */
        for (let i = index - 1; i >= 0; i--) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];
            if (
                elementFocusStatus.questionId === currentElement.questionId &&
                elementFocusStatus.questionId !== 0 &&
                elementFocusStatus.elementRef?.current &&
                isFocusable(elementFocusStatus)
            ) {
                return this.getFocusableElement(elementFocusStatus);
            }
        }

        for (let i = index - 1; i >= 0; i--) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];
            if (
                elementFocusStatus.questionId !== 0 &&
                elementFocusStatus.questionId !== currentElement.questionId &&
                elementFocusStatus.elementRef?.current &&
                isFocusable(elementFocusStatus)
            ) {
                return this.getFocusableElement(elementFocusStatus);
            }
        }

        return null;
    }

    findNextFocusableElementWithinSameVariableCommonForHybridGrid(
        currentElement: DefaultFocusStatusModel,
        eventKey: string,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);
        const isRowCell = (s: DefaultFocusStatusModel) => !s.elementId.endsWith("_OE_INPUT");
        if (eventKey === "ArrowRight") {
            for (let i = index; i < this.defaultFocusStatus.statuses.length; i++) {
                const elementFocusStatus = this.defaultFocusStatus.statuses[i];

                if (
                    elementFocusStatus.questionId === currentElement.questionId &&
                    elementFocusStatus.rowIndex === currentElement.rowIndex &&
                    elementFocusStatus.elementId !== currentElement.elementId &&
                    isRowCell(elementFocusStatus) &&
                    elementFocusStatus.elementRef?.current &&
                    isFocusable(elementFocusStatus)
                ) {
                    return this.getFocusableElement(elementFocusStatus);
                }
            }
        } else {
            for (let i = index; i >= 0; i--) {
                const elementFocusStatus = this.defaultFocusStatus.statuses[i];
                if (
                    elementFocusStatus.questionId === currentElement.questionId &&
                    elementFocusStatus.rowIndex === currentElement.rowIndex &&
                    elementFocusStatus.elementId !== currentElement.elementId &&
                    isRowCell(elementFocusStatus) &&
                    elementFocusStatus.elementRef?.current &&
                    isFocusable(elementFocusStatus)
                ) {
                    return this.getFocusableElement(elementFocusStatus);
                }
            }
        }

        return null;
    }

    findNextFocusableElementWithinSameVariableCommonForSwitchingRowsForHybridGrid(
        currentElement: DefaultFocusStatusModel,
        eventKey: string,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);
        const isRowCell = (s: DefaultFocusStatusModel) => !s.elementId.endsWith("_OE_INPUT");
        const currentQuestionRowIndexes: number[] =
            this.defaultFocusStatus.statuses
                ?.filter((status) => status.questionId === currentElement.questionId)
                .map((s) => s.rowIndex) || [];
        const currentQuestionUniqueRowIndexes = currentQuestionRowIndexes.filter(
            (item, currIndex) => currentQuestionRowIndexes.indexOf(item) === currIndex,
        );

        const currentRowIndex = currentQuestionUniqueRowIndexes.indexOf(currentElement.rowIndex);
        let requiredRowIndex = currentElement.rowIndex;
        const isQuestionLastRowIndex =
            currentRowIndex === currentQuestionUniqueRowIndexes.length - 1;
        const isQuestionFirstRowIndex = currentRowIndex === 0;

        if (eventKey === "ArrowDown" && isQuestionLastRowIndex) {
            return null;
        }
        if (eventKey === "ArrowUp" && isQuestionFirstRowIndex) {
            return null;
        }

        if (
            eventKey === "ArrowDown" &&
            currentRowIndex !== -1 &&
            currentRowIndex < currentQuestionUniqueRowIndexes.length - 1
        ) {
            requiredRowIndex = currentQuestionUniqueRowIndexes[currentRowIndex + 1];
        } else if (eventKey === "ArrowUp" && currentRowIndex > 0) {
            requiredRowIndex = currentQuestionUniqueRowIndexes[currentRowIndex - 1];
        }

        // Get column index within current row (position among cells with same questionId and rowIndex)
        const elementsInCurrentRow = this.defaultFocusStatus.statuses.filter(
            (s, i) =>
                s.questionId === currentElement.questionId &&
                s.rowIndex === currentElement.rowIndex &&
                isRowCell(s) &&
                i <= index,
        );
        const columnIndexInRow = elementsInCurrentRow.length - 1;

        // Get cells in target row, preserving DOM order (skip open-ends)
        const elementsInTargetRow = this.defaultFocusStatus.statuses
            .map((s, i) => ({ status: s, index: i }))
            .filter(
                (x) =>
                    x.status.questionId === currentElement.questionId &&
                    x.status.rowIndex === requiredRowIndex &&
                    isRowCell(x.status) &&
                    x.status.elementId !== currentElement.elementId &&
                    (eventKey === "ArrowDown" ? x.index > index : x.index < index) &&
                    isFocusable(x.status),
            );

        const targetElement =
            elementsInTargetRow[columnIndexInRow] ??
            elementsInTargetRow[elementsInTargetRow.length - 1];
        return targetElement?.status ?? null;
    }

    updateNewFocusStatus(elementWithFocus: DefaultFocusStatusModel) {
        this.defaultFocusStatus.statuses.forEach((element) => {
            element.focusStatus = false;
        });

        const index = this.defaultFocusStatus.statuses.indexOf(elementWithFocus);
        if (index !== -1) {
            this.defaultFocusStatus.statuses[index].focusStatus = true;
            this.dispatch(setDefaultFocusStatus(this.defaultFocusStatus));
        }
    }

    scroll(elementToFocus) {
        const rect = elementToFocus.getBoundingClientRect();
        const isInViewport =
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth);

        if (!isInViewport) {
            elementToFocus.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }

    handleKeyPressEvent(
        event: React.KeyboardEvent<HTMLDivElement>,
        currentElementInfo: DefaultFocusStatusModel,
    ) {
        if (!currentElementInfo) return;

        if (shouldDeferHorizontalGridNavForTextControl(event)) {
            return;
        }

        let nextFocusableElementInfo;

        switch (event.key) {
            case "ArrowLeft":
            case "ArrowRight":
            case "ArrowUp":
            case "ArrowDown":
                nextFocusableElementInfo = this.findNextFocusableElementWithinSameVariable(
                    currentElementInfo,
                    event.key,
                );
                break;
            case "Tab":
                if (event.shiftKey) {
                    nextFocusableElementInfo =
                        this.findLastFocusableElementWithinPreviousVariable(currentElementInfo);
                } else {
                    nextFocusableElementInfo =
                        this.findNextFocusableElementWithinNextVariable(currentElementInfo);
                }
                // For grids (hybrid or otherwise), if no next variable found, try to stay within the grid
                if (nextFocusableElementInfo) {
                    break;
                }
                if (!event.shiftKey) {
                    nextFocusableElementInfo = this.findNextCellWithinSameGrid(currentElementInfo);
                } else {
                    nextFocusableElementInfo =
                        this.findPreviousCellWithinSameGrid(currentElementInfo);
                }
                break;
            default:
                return;
        }

        if (nextFocusableElementInfo && nextFocusableElementInfo.elementRef.current) {
            const elementToFocus = nextFocusableElementInfo.elementRef.current;
            if (elementToFocus && typeof elementToFocus.scrollIntoView === "function") {
                this.scroll(elementToFocus);
            }
            elementToFocus.focus();
            event.preventDefault();
            this.updateNewFocusStatus(nextFocusableElementInfo);
        }
    }

    findNextCellWithinSameGrid(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);
        // Check if we're in a grid by looking for elements with the same questionId
        const elementsInSameQuestion = this.defaultFocusStatus.statuses.filter(
            (el) => el.questionId === currentElement.questionId && el.questionId !== 0,
        );

        // If we have multiple elements with the same questionId, we might be in a grid
        // Try to find the next element in the same grid
        if (elementsInSameQuestion.length > 1) {
            for (let i = index + 1; i < this.defaultFocusStatus.statuses.length; i++) {
                const elementFocusStatus = this.defaultFocusStatus.statuses[i];
                if (
                    elementFocusStatus.questionId === currentElement.questionId &&
                    elementFocusStatus.elementRef?.current &&
                    isFocusable(elementFocusStatus)
                ) {
                    return this.getFocusableElement(elementFocusStatus, true);
                }
            }
        }
        return null;
    }

    findPreviousCellWithinSameGrid(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);
        // Check if we're in a grid by looking for elements with the same questionId
        const elementsInSameQuestion = this.defaultFocusStatus.statuses.filter(
            (el) => el.questionId === currentElement.questionId && el.questionId !== 0,
        );

        // If we have multiple elements with the same questionId, we might be in a grid
        // Try to find the previous element in the same grid
        if (elementsInSameQuestion.length > 1) {
            for (let i = index - 1; i >= 0; i--) {
                const elementFocusStatus = this.defaultFocusStatus.statuses[i];
                if (
                    elementFocusStatus.questionId === currentElement.questionId &&
                    elementFocusStatus.elementRef?.current &&
                    isFocusable(elementFocusStatus)
                ) {
                    return this.getFocusableElement(elementFocusStatus);
                }
            }
        }
        return null;
    }

    getFocusableElement(
        elementFocusStatus: DefaultFocusStatusModel,
        isNextElement: boolean = false,
    ) {
        if (elementFocusStatus.lookupTableGroupType === "lookupTableRow") {
            return this.findFirstFocusableElementTableBody(elementFocusStatus);
        }
        return elementFocusStatus;
    }

    findFirstFocusableElementTableBody(elementFocusStatus: DefaultFocusStatusModel) {
        const parentElement = elementFocusStatus.elementRef.current.closest("tbody");
        const firstElement = parentElement.querySelector(":first-child");
        let index = this.defaultFocusStatus.statuses.findIndex((status) => {
            return status.elementId === firstElement.id;
        });
        if (index === -1) {
            return elementFocusStatus;
        }
        return this.defaultFocusStatus.statuses[index];
    }

    findNextElementInLookupTable(currentElementInfo: DefaultFocusStatusModel) {
        const findTableRowElement = this.defaultFocusStatus.statuses.find(
            (element: DefaultFocusStatusModel) => {
                return (
                    element.elementRef?.current &&
                    element.lookupTableGroupType === "lookupTableRow" &&
                    element.questionId === currentElementInfo.questionId &&
                    element.elementRef?.current?.closest("tbody")
                );
            },
        );
        if (findTableRowElement) {
            return this.findFirstFocusableElementTableBody(findTableRowElement);
        }
    }
}
